// Export-to-PDF button with inline busy + error state.
//
// The button owns the async lifecycle (spinner while generating, inline error
// on failure); the caller supplies `onExport`, an async thunk that gathers the
// data, lazy-imports the PDF module, and triggers the download. There is no
// app-wide toast system, so errors surface inline beneath the button.

import { useTranslation } from '@jq4race/i18n';
import { Button } from '@jq4race/ui';
import { Download, Loader2 } from 'lucide-react';
import { useState } from 'react';
import { translateApiError } from '../../../../shared/errors/translateApiError';

export interface ExportPdfButtonProps {
  onExport: () => Promise<void>;
  label?: string;
  disabled?: boolean;
}

export function ExportPdfButton({ onExport, label, disabled = false }: ExportPdfButtonProps) {
  const { t } = useTranslation('analytics');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolvedLabel = label ?? t('export.buttonLabel');

  async function handleClick() {
    setError(null);
    setBusy(true);
    try {
      await onExport();
    } catch (err) {
      setError(translateApiError(err, t, { fallback: t('errors.exportError') }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="flex flex-col items-end gap-1">
      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={handleClick}
        disabled={disabled || busy}
      >
        {busy ? <Loader2 className="animate-spin" /> : <Download />}
        {busy ? t('export.exporting') : resolvedLabel}
      </Button>
      {error && (
        <span role="alert" className="max-w-[220px] text-right text-[11px] text-jq-danger">
          {error}
        </span>
      )}
    </div>
  );
}
